import React, { useState, useEffect } from 'react';
import { WhatsAppConnection, WhatsAppWebhookConfig } from '../../types/config';
import { apiService } from '../../services/api';

interface WhatsAppAdvancedSettingsProps {
  connection: WhatsAppConnection;
  onSettingsChange: (settings: Partial<WhatsAppConnection>) => void;
  onSaveWebhook?: (webhookSettings: Partial<WhatsAppConnection>) => void;
  webhookSaving?: boolean;
  isConnected: boolean;
}

const WhatsAppAdvancedSettings: React.FC<WhatsAppAdvancedSettingsProps> = ({
  connection,
  onSettingsChange,
  onSaveWebhook,
  webhookSaving = false,
  isConnected
}) => {
  const [activeTab, setActiveTab] = useState<'webhook' | 'profile' | 'privacy' | 'general' | 'api-key'>('webhook');
  const [webhookConfig, setWebhookConfig] = useState<WhatsAppWebhookConfig>({
    enabled: connection.webhook_enabled || false,
    url: connection.webhook_url || '',
    headers: connection.webhook_headers || { 'Content-Type': 'application/json' },
    byEvents: connection.webhook_by_events || false,
    base64: connection.webhook_base64 || false,
    events: connection.webhook_events || []
  });

  const [profileSettings, setProfileSettings] = useState({
    profile_name: connection.profile_name || '',
    profile_status: connection.profile_status || '',
    profile_picture_url: connection.profile_picture_url || ''
  });

  const [privacySettings, setPrivacySettings] = useState({
    readreceipts: connection.privacy_settings?.readreceipts || 'all',
    profile: connection.privacy_settings?.profile || 'all',
    status: connection.privacy_settings?.status || 'contacts',
    online: connection.privacy_settings?.online || 'all',
    last: connection.privacy_settings?.last || 'contacts',
    groupadd: connection.privacy_settings?.groupadd || 'none'
  });

  const [generalSettings, setGeneralSettings] = useState({
    rejectCall: connection.settings?.rejectCall ?? true,
    msgCall: connection.settings?.msgCall || 'Não aceito chamadas',
    groupsIgnore: connection.settings?.groupsIgnore ?? false,
    alwaysOnline: connection.settings?.alwaysOnline ?? true,
    readMessages: connection.settings?.readMessages ?? false,
    syncFullHistory: connection.settings?.syncFullHistory ?? false,
    readStatus: connection.settings?.readStatus ?? false
  });

  const [presence, setPresence] = useState(connection.presence || 'available');

  // Estados para gerenciamento de API key
  const [apiKey, setApiKey] = useState<string>(connection.api_key || '');
  const [showApiKey, setShowApiKey] = useState<boolean>(false);
  const [isLoadingApiKey, setIsLoadingApiKey] = useState<boolean>(false);
  const [isSavingApiKey, setIsSavingApiKey] = useState<boolean>(false);
  const [customApiKey, setCustomApiKey] = useState<string>('');
  const [showCustomInput, setShowCustomInput] = useState<boolean>(false);

  // Eventos disponíveis para webhook (27 eventos do Baileys)
  const availableEvents = [
    // Conexão e Autenticação
    { key: 'APPLICATION_STARTUP', label: '🚀 Inicialização da Aplicação' },
    { key: 'QRCODE_UPDATED', label: '📱 QR Code Atualizado' },
    { key: 'CONNECTION_UPDATE', label: '🔌 Conexão Atualizada' },

    // Mensagens
    { key: 'MESSAGES_SET', label: '💬 Mensagens Definidas (Histórico)' },
    { key: 'MESSAGES_UPSERT', label: '💬 Mensagens Criadas/Recebidas' },
    { key: 'MESSAGES_UPDATE', label: '✏️ Mensagens Atualizadas' },
    { key: 'MESSAGES_DELETE', label: '🗑️ Mensagens Excluídas' },
    { key: 'MESSAGES_REACTION', label: '❤️ Reações em Mensagens' },
    { key: 'MESSAGE_RECEIPT_UPDATE', label: '✓✓ Confirmações de Leitura' },
    { key: 'SEND_MESSAGE', label: '📤 Enviar Mensagem (API)' },

    // Contatos
    { key: 'CONTACTS_SET', label: '👤 Contatos Definidos' },
    { key: 'CONTACTS_UPSERT', label: '👤 Contatos Criados/Atualizados' },
    { key: 'CONTACTS_UPDATE', label: '👤 Contatos Atualizados' },

    // Conversas
    { key: 'CHATS_SET', label: '💭 Conversas Definidas' },
    { key: 'CHATS_UPSERT', label: '💭 Conversas Criadas/Atualizadas' },
    { key: 'CHATS_UPDATE', label: '💭 Conversas Atualizadas' },
    { key: 'CHATS_DELETE', label: '💭 Conversas Excluídas' },

    // Grupos
    { key: 'GROUPS_UPSERT', label: '👥 Grupos Criados/Atualizados' },
    { key: 'GROUP_UPDATE', label: '👥 Grupo Atualizado' },
    { key: 'GROUP_PARTICIPANTS_UPDATE', label: '👥 Participantes Atualizados' },

    // Outros
    { key: 'PRESENCE_UPDATE', label: '🟢 Presença Atualizada (Online/Offline)' },
    { key: 'LABELS_EDIT', label: '🏷️ Etiquetas Editadas' },
    { key: 'LABELS_ASSOCIATION', label: '🏷️ Associação de Etiquetas' },
    { key: 'CALL', label: '📞 Chamada Recebida' },
    { key: 'MESSAGING_HISTORY_SET', label: '📜 Sincronização de Histórico' },
    { key: 'BLOCKLIST_SET', label: '🚫 Lista de Bloqueados' },
    { key: 'BLOCKLIST_UPDATE', label: '🚫 Bloqueio/Desbloqueio' }
  ];

  // Atualizar configurações quando a conexão mudar
  useEffect(() => {
    setWebhookConfig({
      enabled: connection.webhook_enabled || false,
      url: connection.webhook_url || '',
      headers: connection.webhook_headers || { 'Content-Type': 'application/json' },
      byEvents: connection.webhook_by_events || false,
      base64: connection.webhook_base64 || false,
      events: connection.webhook_events || []
    });

    setProfileSettings({
      profile_name: connection.profile_name || '',
      profile_status: connection.profile_status || '',
      profile_picture_url: connection.profile_picture_url || ''
    });

    setPrivacySettings({
      readreceipts: connection.privacy_settings?.readreceipts || 'all',
      profile: connection.privacy_settings?.profile || 'all',
      status: connection.privacy_settings?.status || 'contacts',
      online: connection.privacy_settings?.online || 'all',
      last: connection.privacy_settings?.last || 'contacts',
      groupadd: connection.privacy_settings?.groupadd || 'none'
    });

    setGeneralSettings({
      rejectCall: connection.settings?.rejectCall || true,
      msgCall: connection.settings?.msgCall || 'Não aceito chamadas',
      groupsIgnore: connection.settings?.groupsIgnore || false,
      alwaysOnline: connection.settings?.alwaysOnline || true,
      readMessages: connection.settings?.readMessages || false,
      syncFullHistory: connection.settings?.syncFullHistory || false,
      readStatus: connection.settings?.readStatus || false
    });

    setPresence(connection.presence || 'available');
    setApiKey(connection.api_key || '');
  }, [connection]);

  // Carregar API key quando a aba for aberta
  useEffect(() => {
    if (activeTab === 'api-key' && connection.id && !apiKey) {
      loadApiKey();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Funções para atualizar configurações (sem salvar automaticamente)
  const updateWebhookConfig = (updates: Partial<WhatsAppWebhookConfig>) => {
    const newConfig = { ...webhookConfig, ...updates };
    setWebhookConfig(newConfig);
  };

  const updateProfileSettings = (updates: Partial<typeof profileSettings>) => {
    const newSettings = { ...profileSettings, ...updates };
    setProfileSettings(newSettings);
  };

  const updatePrivacySettings = (updates: Partial<typeof privacySettings>) => {
    const newSettings = { ...privacySettings, ...updates };
    setPrivacySettings(newSettings);
  };

  const updateGeneralSettings = (updates: Partial<typeof generalSettings>) => {
    const newSettings = { ...generalSettings, ...updates };
    setGeneralSettings(newSettings);
  };

  const updatePresence = (newPresence: string) => {
    setPresence(newPresence);
  };

  // Funções para gerenciar API key
  const loadApiKey = async () => {
    if (!connection.id) return;

    setIsLoadingApiKey(true);
    try {
      const response = await apiService.getWhatsAppApiKey(connection.id);
      if (response.success && response.data) {
        setApiKey(response.data.api_key || '');
      }
    } catch (error: any) {
      console.error('Erro ao carregar API key:', error);
      alert(error.message || 'Erro ao carregar API key');
    } finally {
      setIsLoadingApiKey(false);
    }
  };

  const copyApiKeyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      alert('API key copiada para a área de transferência!');
    } catch (error) {
      console.error('Erro ao copiar API key:', error);
      alert('Erro ao copiar API key. Tente selecionar e copiar manualmente.');
    }
  };

  const generateNewApiKey = async () => {
    if (!connection.id) return;

    const confirmed = window.confirm(
      'Tem certeza que deseja gerar uma nova API key?\n\n' +
      'ATENÇÃO: A API key atual será invalidada e você precisará atualizar todos os sistemas que a utilizam.\n\n' +
      'Esta ação não pode ser desfeita.'
    );

    if (!confirmed) return;

    setIsSavingApiKey(true);
    try {
      const response = await apiService.generateWhatsAppApiKey(connection.id);
      if (response.success && response.data) {
        setApiKey(response.data.api_key);
        setShowApiKey(true);
        alert('Nova API key gerada com sucesso!');
      }
    } catch (error: any) {
      console.error('Erro ao gerar nova API key:', error);
      alert(error.message || 'Erro ao gerar nova API key');
    } finally {
      setIsSavingApiKey(false);
    }
  };

  const saveCustomApiKey = async () => {
    if (!connection.id) return;

    if (!customApiKey || customApiKey.trim().length < 32) {
      alert('A API key deve ter pelo menos 32 caracteres');
      return;
    }

    // Validar formato hexadecimal
    const hexRegex = /^[a-f0-9]+$/i;
    if (!hexRegex.test(customApiKey)) {
      alert('A API key deve conter apenas caracteres hexadecimais (0-9, a-f)');
      return;
    }

    setIsSavingApiKey(true);
    try {
      const response = await apiService.updateWhatsAppApiKey(connection.id, customApiKey);
      if (response.success && response.data) {
        setApiKey(response.data.api_key);
        setCustomApiKey('');
        setShowCustomInput(false);
        setShowApiKey(true);
        alert('API key atualizada com sucesso!');
      }
    } catch (error: any) {
      console.error('Erro ao atualizar API key:', error);
      alert(error.message || 'Erro ao atualizar API key');
    } finally {
      setIsSavingApiKey(false);
    }
  };

  // Funções para salvar configurações
  const saveWebhookSettings = () => {
    const webhookSettings = {
      webhook_enabled: webhookConfig.enabled,
      webhook_url: webhookConfig.url,
      webhook_headers: webhookConfig.headers,
      webhook_by_events: webhookConfig.byEvents,
      webhook_base64: webhookConfig.base64,
      webhook_events: webhookConfig.events
    };

    // Usar onSaveWebhook se disponível, caso contrário usar onSettingsChange
    if (onSaveWebhook) {
      onSaveWebhook(webhookSettings);
    } else {
      onSettingsChange(webhookSettings);
    }
  };

  const saveProfileSettings = () => {
    onSettingsChange({
      profile_name: profileSettings.profile_name,
      profile_status: profileSettings.profile_status,
      profile_picture_url: profileSettings.profile_picture_url
    });
  };

  const savePrivacySettings = () => {
    onSettingsChange({
      privacy_settings: privacySettings
    });
  };

  const saveGeneralSettings = () => {
    onSettingsChange({
      settings: generalSettings,
      presence: presence
    });
  };

  // Funções auxiliares
  const handleEventToggle = (eventKey: string) => {
    const newEvents = webhookConfig.events.includes(eventKey)
      ? webhookConfig.events.filter(e => e !== eventKey)
      : [...webhookConfig.events, eventKey];
    
    updateWebhookConfig({ events: newEvents });
  };

  const handleHeaderChange = (key: string, value: string) => {
    const newHeaders = { ...webhookConfig.headers, [key]: value };
    updateWebhookConfig({ headers: newHeaders });
  };

  const addHeader = () => {
    const newHeaders = { ...webhookConfig.headers, '': '' };
    updateWebhookConfig({ headers: newHeaders });
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...webhookConfig.headers };
    delete newHeaders[key];
    updateWebhookConfig({ headers: newHeaders });
  };

  return (
    <div className="whatsapp-advanced-settings">
      <div className="settings-tabs">
        <button
          type="button"
          className={`tab-button ${activeTab === 'webhook' ? 'active' : ''}`}
          onClick={() => setActiveTab('webhook')}
        >
          <i className="fas fa-link"></i>
          Webhook
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <i className="fas fa-user"></i>
          Perfil
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'privacy' ? 'active' : ''}`}
          onClick={() => setActiveTab('privacy')}
        >
          <i className="fas fa-shield-alt"></i>
          Privacidade
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          <i className="fas fa-cog"></i>
          Geral
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'api-key' ? 'active' : ''}`}
          onClick={() => setActiveTab('api-key')}
        >
          <i className="fas fa-key"></i>
          API Key
        </button>
      </div>

      <div className="settings-content">
        {/* Configurações de Webhook */}
        {activeTab === 'webhook' && (
          <div className="webhook-settings">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={webhookConfig.enabled}
                  onChange={(e) => updateWebhookConfig({ enabled: e.target.checked })}
                />
                <span>Habilitar Webhook</span>
              </label>
            </div>

            {webhookConfig.enabled && (
              <>
                <div className="form-group">
                  <label>URL do Webhook</label>
                  <input
                    type="url"
                    value={webhookConfig.url}
                    onChange={(e) => updateWebhookConfig({ url: e.target.value })}
                    placeholder="https://webhook.site/seu-endpoint"
                  />
                </div>

                <div className="form-group">
                  <label>Headers</label>
                  {Object.entries(webhookConfig.headers).map(([key, value]) => (
                    <div key={key} className="header-input">
                      <input
                        type="text"
                        placeholder="Chave"
                        value={key}
                        onChange={(e) => {
                          const newHeaders = { ...webhookConfig.headers };
                          delete newHeaders[key];
                          newHeaders[e.target.value] = value;
                          updateWebhookConfig({ headers: newHeaders });
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Valor"
                        value={value}
                        onChange={(e) => handleHeaderChange(key, e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeHeader(key)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={addHeader}
                  >
                    <i className="fas fa-plus"></i> Adicionar Header
                  </button>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={webhookConfig.byEvents}
                      onChange={(e) => updateWebhookConfig({ byEvents: e.target.checked })}
                    />
                    <span>Enviar apenas eventos específicos</span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={webhookConfig.base64}
                      onChange={(e) => updateWebhookConfig({ base64: e.target.checked })}
                    />
                    <span>Enviar mídia em Base64</span>
                  </label>
                </div>

                {webhookConfig.byEvents && (
                  <div className="form-group">
                    <label>Eventos para Enviar</label>
                    <div className="events-grid">
                      {availableEvents.map(event => (
                        <label key={event.key} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={webhookConfig.events.includes(event.key)}
                            onChange={() => handleEventToggle(event.key)}
                          />
                          <span>{event.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="settings-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveWebhookSettings}
                disabled={webhookSaving}
              >
                {webhookSaving ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Salvando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i> Salvar Configurações de Webhook
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Configurações de Perfil */}
        {activeTab === 'profile' && (
          <div className="profile-settings">
            <div className="form-group">
              <label>Nome do Perfil</label>
              <input
                type="text"
                value={profileSettings.profile_name}
                onChange={(e) => updateProfileSettings({ profile_name: e.target.value })}
                placeholder="Nome exibido no WhatsApp"
                disabled={!isConnected}
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <textarea
                value={profileSettings.profile_status}
                onChange={(e) => updateProfileSettings({ profile_status: e.target.value })}
                placeholder="Status do perfil"
                rows={3}
                disabled={!isConnected}
              />
            </div>

            <div className="form-group">
              <label>URL da Foto de Perfil</label>
              <input
                type="url"
                value={profileSettings.profile_picture_url}
                onChange={(e) => updateProfileSettings({ profile_picture_url: e.target.value })}
                placeholder="https://exemplo.com/foto.jpg"
                disabled={!isConnected}
              />
            </div>

            {!isConnected && (
              <div className="alert alert-info">
                <i className="fas fa-info-circle"></i>
                Para alterar as configurações de perfil, a conexão deve estar ativa.
              </div>
            )}

            <div className="settings-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveProfileSettings}
                disabled={!isConnected}
              >
                <i className="fas fa-save"></i> Salvar Configurações de Perfil
              </button>
            </div>
          </div>
        )}

        {/* Configurações de Privacidade */}
        {activeTab === 'privacy' && (
          <div className="privacy-settings">
            <div className="form-group">
              <label>Confirmações de Leitura</label>
              <select
                value={privacySettings.readreceipts}
                onChange={(e) => updatePrivacySettings({ readreceipts: e.target.value })}
                disabled={!isConnected}
              >
                <option value="all">Todos</option>
                <option value="contacts">Apenas Contatos</option>
                <option value="none">Ninguém</option>
              </select>
            </div>

            <div className="form-group">
              <label>Foto do Perfil</label>
              <select
                value={privacySettings.profile}
                onChange={(e) => updatePrivacySettings({ profile: e.target.value })}
                disabled={!isConnected}
              >
                <option value="all">Todos</option>
                <option value="contacts">Apenas Contatos</option>
                <option value="none">Ninguém</option>
              </select>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={privacySettings.status}
                onChange={(e) => updatePrivacySettings({ status: e.target.value })}
                disabled={!isConnected}
              >
                <option value="all">Todos</option>
                <option value="contacts">Apenas Contatos</option>
                <option value="none">Ninguém</option>
              </select>
            </div>

            <div className="form-group">
              <label>Status Online</label>
              <select
                value={privacySettings.online}
                onChange={(e) => updatePrivacySettings({ online: e.target.value })}
                disabled={!isConnected}
              >
                <option value="all">Todos</option>
                <option value="contacts">Apenas Contatos</option>
                <option value="none">Ninguém</option>
              </select>
            </div>

            <div className="form-group">
              <label>Última Vez Online</label>
              <select
                value={privacySettings.last}
                onChange={(e) => updatePrivacySettings({ last: e.target.value })}
                disabled={!isConnected}
              >
                <option value="all">Todos</option>
                <option value="contacts">Apenas Contatos</option>
                <option value="none">Ninguém</option>
              </select>
            </div>

            <div className="form-group">
              <label>Adicionar a Grupos</label>
              <select
                value={privacySettings.groupadd}
                onChange={(e) => updatePrivacySettings({ groupadd: e.target.value })}
                disabled={!isConnected}
              >
                <option value="all">Todos</option>
                <option value="contacts">Apenas Contatos</option>
                <option value="none">Ninguém</option>
              </select>
            </div>

            {!isConnected && (
              <div className="alert alert-info">
                <i className="fas fa-info-circle"></i>
                Para alterar as configurações de privacidade, a conexão deve estar ativa.
              </div>
            )}

            <div className="settings-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={savePrivacySettings}
                disabled={!isConnected}
              >
                <i className="fas fa-save"></i> Salvar Configurações de Privacidade
              </button>
            </div>
          </div>
        )}

        {/* Configurações Gerais */}
        {activeTab === 'general' && (
          <div className="general-settings">
            <div className="form-group">
              <label>Presença</label>
              <select
                value={presence}
                onChange={(e) => updatePresence(e.target.value)}
                disabled={!isConnected}
              >
                <option value="available">Disponível</option>
                <option value="unavailable">Indisponível</option>
                <option value="composing">Digitando</option>
                <option value="recording">Gravando</option>
                <option value="paused">Pausado</option>
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={generalSettings.rejectCall}
                  onChange={(e) => updateGeneralSettings({ rejectCall: e.target.checked })}
                  disabled={!isConnected}
                />
                <span>Rejeitar Chamadas</span>
              </label>
            </div>

            {generalSettings.rejectCall && (
              <div className="form-group">
                <label>Mensagem de Rejeição</label>
                <input
                  type="text"
                  value={generalSettings.msgCall}
                  onChange={(e) => updateGeneralSettings({ msgCall: e.target.value })}
                  placeholder="Mensagem para chamadas rejeitadas"
                  disabled={!isConnected}
                />
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={generalSettings.groupsIgnore}
                  onChange={(e) => updateGeneralSettings({ groupsIgnore: e.target.checked })}
                  disabled={!isConnected}
                />
                <span>Ignorar Grupos</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={generalSettings.alwaysOnline}
                  onChange={(e) => updateGeneralSettings({ alwaysOnline: e.target.checked })}
                  disabled={!isConnected}
                />
                <span>Sempre Online</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={generalSettings.readMessages}
                  onChange={(e) => updateGeneralSettings({ readMessages: e.target.checked })}
                  disabled={!isConnected}
                />
                <span>Marcar Mensagens como Lidas</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={generalSettings.syncFullHistory}
                  onChange={(e) => updateGeneralSettings({ syncFullHistory: e.target.checked })}
                  disabled={!isConnected}
                />
                <span>Sincronizar Histórico Completo</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={generalSettings.readStatus}
                  onChange={(e) => updateGeneralSettings({ readStatus: e.target.checked })}
                  disabled={!isConnected}
                />
                <span>Ler Status</span>
              </label>
            </div>

            {!isConnected && (
              <div className="alert alert-info">
                <i className="fas fa-info-circle"></i>
                Para alterar as configurações gerais, a conexão deve estar ativa.
              </div>
            )}

            <div className="settings-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveGeneralSettings}
                disabled={!isConnected}
              >
                <i className="fas fa-save"></i> Salvar Configurações Gerais
              </button>
            </div>
          </div>
        )}

        {/* Configurações de API Key */}
        {activeTab === 'api-key' && (
          <div className="api-key-settings">
            <div className="alert alert-info">
              <i className="fas fa-info-circle"></i>
              <div>
                <strong>API Key de Autenticação</strong>
                <p>
                  Use esta chave para autenticar requisições aos endpoints da API do WhatsApp.
                  Envie a API key no header <code>apikey</code> ou <code>X-API-Key</code>.
                </p>
              </div>
            </div>

            {isLoadingApiKey ? (
              <div className="loading-container">
                <i className="fas fa-spinner fa-spin"></i> Carregando API key...
              </div>
            ) : (
              <>
                {apiKey ? (
                  <div className="api-key-display">
                    <div className="form-group">
                      <label>API Key Atual</label>
                      <div className="api-key-input-group">
                        <input
                          type={showApiKey ? 'text' : 'password'}
                          value={apiKey}
                          readOnly
                          className="api-key-input"
                        />
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setShowApiKey(!showApiKey)}
                          title={showApiKey ? 'Ocultar API key' : 'Mostrar API key'}
                        >
                          <i className={`fas ${showApiKey ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={copyApiKeyToClipboard}
                          title="Copiar API key"
                        >
                          <i className="fas fa-copy"></i> Copiar
                        </button>
                      </div>
                    </div>

                    <div className="api-key-actions">
                      <button
                        type="button"
                        className="btn btn-warning"
                        onClick={generateNewApiKey}
                        disabled={isSavingApiKey}
                      >
                        {isSavingApiKey ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i> Gerando...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-sync-alt"></i> Gerar Nova API Key
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowCustomInput(!showCustomInput)}
                      >
                        <i className="fas fa-edit"></i> Definir API Key Personalizada
                      </button>
                    </div>

                    {showCustomInput && (
                      <div className="custom-api-key-input">
                        <div className="form-group">
                          <label>API Key Personalizada</label>
                          <input
                            type="text"
                            value={customApiKey}
                            onChange={(e) => setCustomApiKey(e.target.value)}
                            placeholder="Insira uma API key personalizada (mínimo 32 caracteres hexadecimais)"
                          />
                          <small className="form-hint">
                            A API key deve conter apenas caracteres hexadecimais (0-9, a-f) e ter entre 32 e 128 caracteres.
                          </small>
                        </div>
                        <div className="custom-api-key-actions">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={saveCustomApiKey}
                            disabled={isSavingApiKey || !customApiKey}
                          >
                            {isSavingApiKey ? (
                              <>
                                <i className="fas fa-spinner fa-spin"></i> Salvando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save"></i> Salvar API Key Personalizada
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              setShowCustomInput(false);
                              setCustomApiKey('');
                            }}
                          >
                            <i className="fas fa-times"></i> Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-api-key">
                    <p>Nenhuma API key encontrada para esta instância.</p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={generateNewApiKey}
                      disabled={isSavingApiKey}
                    >
                      {isSavingApiKey ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Gerando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-key"></i> Gerar API Key
                        </>
                      )}
                    </button>
                  </div>
                )}

                <div className="api-key-documentation">
                  <h4>Como Usar a API Key</h4>
                  <p>Envie a API key em uma das seguintes formas:</p>

                  <div className="code-example">
                    <strong>Header HTTP:</strong>
                    <pre><code>apikey: {apiKey || 'SUA_API_KEY_AQUI'}</code></pre>
                    <p>ou</p>
                    <pre><code>X-API-Key: {apiKey || 'SUA_API_KEY_AQUI'}</code></pre>
                  </div>

                  <div className="code-example">
                    <strong>Exemplo com cURL:</strong>
                    <pre><code>{`curl -X POST https://api.exemplo.com/whatsapp/send \\
  -H "apikey: ${apiKey || 'SUA_API_KEY_AQUI'}" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Olá!"}'`}</code></pre>
                  </div>

                  <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle"></i>
                    <strong>Segurança:</strong> Nunca compartilhe sua API key publicamente ou a exponha em código do lado do cliente.
                    Mantenha-a segura e use apenas em servidores backend.
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppAdvancedSettings;
